import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import ActionButton from '../ActionButton/ActionButton';
import InputNumberField from '../InputNumberField/InputNumberField';
import OptionList from '../OptionList/OptionList';
import RangeNavigation from '../RangeNavigation/RangeNavigation';
import SelectOptions from '../SelectOptions/SelectOptions';
import SingleItemNavigation from '../SingleItemNavigation/SingleItemNavigation';
import TypesSelect from '../TypesSelect/TypesSelect';

import AnnotationIcon from '../../assets/svgs/AnnotationIcon';
import ArrowIcon from '../../assets/svgs/ArrowIcon';
import BrushIcon from '../../assets/svgs/BrushIcon';
import CheckIcon from '../../assets/svgs/CheckIcon';
import EraserIcon from '../../assets/svgs/EraserIcon';
import RefreshIcon from '../../assets/svgs/RefreshIcon';
import ZoomInIcon from '../../assets/svgs/ZoomInIcon';
import ZoomOutIcon from '../../assets/svgs/ZoomOutIcon';

import translate from '../../libs/i18n';
import roundToPrecision from '../../utils/roundToPrecision';
import toTitleCase from '../../utils/toTitleCase';
import { useBreakpoint } from '../../utils/useBreakpoint';
import {
  annotationTypeShape,
  functionType,
  idTypes,
  optionShape,
} from '../../utils/propTypesShapes';
import EDIT_TOOL_TYPES from '../../data/edit-tool-types';
import SEGMENTATION_LUNG_TYPES from '../../data/segmentation-lung-types';

import { EditToolListStyle } from './EditToolListStyle';

const MIN_SPHERE_DIAMETER = 1;
const MAX_SPHERE_DIAMETER = 10;
const MIN_DENSITY_MIN_LIMITS = 0;
const MAX_DENSITY_MIN_LIMITS = 100;

const EditToolList = ({
  annotationListAmount,
  eraser,
  imagesAmount,
  lastExamId,
  maxDensityLimits,
  minDensityLimits,
  nextExamId,
  reportId,
  segmentationDiameter,
  segmentationLung,
  selectedSubtype,
  selectedTool,
  selectedType,
  slice,
  zoom,
  annotationTypeOptions,
  reportOptions,
  updateEraser,
  updateFileName,
  updateMaxDensityLimits,
  updateMinDensityLimits,
  updateSegmentationDiameter,
  updateSegmentationLung,
  handleResetZoom,
  handleSave,
  handleSelectType,
  handleZoomIn,
  handleZoomOut,
  updateSelectedSubtype,
  updateSelectedTool,
  updateSlice,
}) => {
  const history = useHistory();
  const breakpoint = useBreakpoint();
  const language = useSelector((state) => state.settings.language);

  const onChangeReport = (examId) => {
    history.push(`/editar/${examId}`);
    history.go(`/editar/${examId}`);
  };

  const onClickAnnotation = () => {
    let currentSelectedTool = '';
    let currentFileName = '';

    if (selectedTool !== EDIT_TOOL_TYPES?.annotation?.key) {
      currentSelectedTool = EDIT_TOOL_TYPES?.annotation?.key;
      currentFileName = toTitleCase(EDIT_TOOL_TYPES?.annotation?.key);
    }

    updateSelectedTool(currentSelectedTool);
    updateFileName(currentFileName);
  };

  const onClickSegmentation = () => {
    let currentSelectedTool = '';
    let currentFileName = '';

    if (selectedTool !== EDIT_TOOL_TYPES?.segmentation?.key) {
      currentSelectedTool = EDIT_TOOL_TYPES?.segmentation?.key;
      currentFileName = toTitleCase(EDIT_TOOL_TYPES?.segmentation?.key);
    }

    updateSelectedTool(currentSelectedTool);
    updateFileName(currentFileName);
  };

  const onClickEraser = () => updateEraser(!eraser);

  return (
    <EditToolListStyle>
      <div className="tools-list-row main-tools-row">
        <div className="select-exam-container">
          {reportOptions?.length ? (
            <SelectOptions
              theme="dark"
              options={reportOptions}
              value={reportId}
              onChange={onChangeReport}
            />
          ) : null}
        </div>
        <div className="select-tool-container">
          <p className="tool-label">Edit:</p>
          {updateEraser && updateSelectedTool ? (
            <OptionList
              theme="dark"
              rounded
              options={[
                {
                  key: 1,
                  content: <AnnotationIcon />,
                  active: selectedTool === EDIT_TOOL_TYPES?.annotation?.key,
                  onClick: onClickAnnotation,
                },
                {
                  key: 2,
                  content: <BrushIcon />,
                  active: selectedTool === EDIT_TOOL_TYPES?.segmentation?.key,
                  onClick: onClickSegmentation,
                },
                {
                  key: 3,
                  content: <EraserIcon />,
                  active: eraser,
                  onClick: onClickEraser,
                },
              ]}
            />
          ) : null}
        </div>
        <div className="select-slice-container">
          <p className="tool-label">Slices:</p>
          <div className="navigation-slice-wrapper">
            <div className="single-item-navigation-wrapper">
              {updateSlice ? (
                <SingleItemNavigation
                  theme="dark"
                  rounded
                  value={slice}
                  minimum={1}
                  maximum={imagesAmount}
                  handleChange={updateSlice}
                />
              ) : null}
            </div>
            <div className="range-navigation-wrapper">
              {updateSlice ? (
                <RangeNavigation
                  theme="light"
                  value={slice}
                  minimum={1}
                  maximum={imagesAmount}
                  handleChange={updateSlice}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="tools-list-row scale-navigation-tools-rows">
        <div className="zoom-tool-container">
          <p className="tool-label">Visualize:</p>
          {handleResetZoom && handleZoomIn && handleZoomOut ? (
            <OptionList
              theme="dark"
              rounded
              options={[
                {
                  key: 1,
                  content: <RefreshIcon />,
                  disabled: !selectedTool?.length,
                  onClick: handleResetZoom,
                },
                {
                  key: 2,
                  content: <ZoomInIcon />,
                  disabled: !selectedTool?.length,
                  onClick: handleZoomIn,
                },
                {
                  key: 3,
                  content: <ZoomOutIcon />,
                  disabled: !selectedTool?.length || zoom <= 0,
                  onClick: handleZoomOut,
                },
                {
                  key: 4,
                  content: `${roundToPrecision(zoom * 100, 0)}%`,
                },
              ]}
            />
          ) : null}
        </div>
        <div className="navigation-container">
          <div className="last-navigation-link">
            <ActionButton
              theme="very-dark"
              text={
                breakpoint?.name === 'full-hd'
                  ? translate('previous', language)
                  : ''
              }
              icon={<ArrowIcon />}
              disabled={lastExamId <= -1}
              onClick={() => onChangeReport(lastExamId)}
            />
          </div>
          <div className="next-navigation-link">
            <ActionButton
              theme="very-dark"
              text={
                breakpoint?.name === 'full-hd'
                  ? translate('next', language)
                  : ''
              }
              icon={<ArrowIcon style={{ transform: 'rotate(-180deg)' }} />}
              iconPosition="right"
              disabled={nextExamId <= -1}
              onClick={() => onChangeReport(nextExamId)}
            />
          </div>
        </div>
      </div>
      {selectedTool === EDIT_TOOL_TYPES?.annotation?.key ? (
        <div className="tools-list-row annotation-tools-rows">
          <div className="types-select-container">
            <p className="tool-label">Annotation Tool</p>
            <div className="select-list-wrapper">
              {annotationTypeOptions?.length ? (
                <div className="select-type-wrapper">
                  {handleSelectType ? (
                    <TypesSelect
                      types={annotationTypeOptions}
                      selected={selectedType}
                      placeholder="Select Type"
                      disabled={!selectedTool?.length}
                      handleSelect={handleSelectType}
                    />
                  ) : null}
                </div>
              ) : null}
              <div className="select-subtype-wrapper">
                {updateSelectedSubtype && selectedType?.subtypes?.length ? (
                  <TypesSelect
                    types={selectedType?.subtypes}
                    selected={selectedSubtype}
                    parent={selectedType}
                    placeholder="Select Sub-Type"
                    handleSelect={updateSelectedSubtype}
                  />
                ) : (
                  <TypesSelect
                    types={[]}
                    selected={selectedSubtype}
                    parent={selectedType}
                    placeholder="Select Sub-Type"
                    disabled
                    handleSelect={() => {}}
                  />
                )}
              </div>
              <div className="save-button-wrapper">
                {handleSave ? (
                  <ActionButton
                    theme="default"
                    text="Save"
                    icon={<CheckIcon />}
                    iconPosition="left"
                    disabled={!annotationListAmount}
                    onClick={handleSave}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {selectedTool === EDIT_TOOL_TYPES?.segmentation?.key ? (
        <div className="tools-list-row segmentation-tools-rows">
          <p className="tool-label tool-label-bold">Segmentation Tool</p>
          <p className="tool-label">Sphere Diameter:</p>
          <div className="diameter-input-wrapper">
            {updateSegmentationDiameter ? (
              <InputNumberField
                theme="dark"
                type="number"
                inputName="segmentation-diameter"
                value={segmentationDiameter}
                handleChange={updateSegmentationDiameter}
                suffix="mm"
                max={MAX_SPHERE_DIAMETER}
                min={MIN_SPHERE_DIAMETER}
              />
            ) : null}
          </div>
          <div className="diameter-range-wrapper">
            {updateSegmentationDiameter ? (
              <RangeNavigation
                theme="light"
                value={segmentationDiameter}
                minimum={MIN_SPHERE_DIAMETER}
                maximum={MAX_SPHERE_DIAMETER}
                step={1}
                handleChange={updateSegmentationDiameter}
              />
            ) : null}
          </div>
          <p className="tool-label">Density Limits:</p>
          <div className="density-limits-wrapper">
            <div className="density-limits-minimum-wrapper">
              <p className="density-limits-label">min</p>
              {updateMinDensityLimits ? (
                <RangeNavigation
                  theme="light"
                  value={minDensityLimits}
                  minimum={MIN_DENSITY_MIN_LIMITS}
                  maximum={MAX_DENSITY_MIN_LIMITS}
                  handleChange={updateMinDensityLimits}
                />
              ) : null}
              <p className="density-limits-value">{minDensityLimits}</p>
            </div>
            <div className="density-limits-maximum-wrapper">
              <p className="density-limits-label">max</p>
              {updateMaxDensityLimits ? (
                <RangeNavigation
                  theme="light"
                  value={maxDensityLimits}
                  minimum={MIN_DENSITY_MIN_LIMITS}
                  maximum={MAX_DENSITY_MIN_LIMITS}
                  handleChange={updateMaxDensityLimits}
                />
              ) : null}
              <p className="density-limits-value">{maxDensityLimits}</p>
            </div>
          </div>
          <p className="tool-label">Select Lung:</p>
          {updateSegmentationLung ? (
            <button
              className={`select-lung left-lung${
                segmentationLung === SEGMENTATION_LUNG_TYPES?.left?.key
                  ? ' active'
                  : ''
              }`}
              type="button"
              onClick={() =>
                updateSegmentationLung(SEGMENTATION_LUNG_TYPES?.left?.key)
              }
            >
              <span className="box" />
              Left
            </button>
          ) : null}
          {updateSegmentationLung ? (
            <button
              className={`select-lung right-lung${
                segmentationLung === SEGMENTATION_LUNG_TYPES?.right?.key
                  ? ' active'
                  : ''
              }`}
              type="button"
              onClick={() =>
                updateSegmentationLung(SEGMENTATION_LUNG_TYPES?.right?.key)
              }
            >
              <span className="box" />
              Right
            </button>
          ) : null}
          <div className="save-button-wrapper">
            {handleSave ? (
              <ActionButton
                theme="default"
                text="Save"
                icon={<CheckIcon />}
                iconPosition="left"
                onClick={handleSave}
              />
            ) : null}
          </div>
        </div>
      ) : null}
    </EditToolListStyle>
  );
};

EditToolList.propTypes = {
  annotationListAmount: PropTypes.number,
  imagesAmount: PropTypes.number,
  maxDensityLimits: PropTypes.number,
  minDensityLimits: PropTypes.number,
  segmentationDiameter: PropTypes.number,
  slice: PropTypes.number,
  zoom: PropTypes.number,
  eraser: PropTypes.bool,
  lastExamId: idTypes,
  nextExamId: idTypes,
  reportId: idTypes,
  segmentationLung: PropTypes.oneOf([
    '',
    ...Object.keys(SEGMENTATION_LUNG_TYPES),
  ]),
  selectedSubtype: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    annotationTypeShape,
  ]),
  selectedTool: PropTypes.oneOf(['', ...Object.keys(EDIT_TOOL_TYPES)]),
  selectedType: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    annotationTypeShape,
  ]),
  annotationTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: idTypes,
      color: PropTypes.string,
      name: PropTypes.string,
      subtypes: PropTypes.arrayOf(
        PropTypes.shape({
          id: idTypes,
          color: PropTypes.string,
          name: PropTypes.string,
        })
      ),
    })
  ),
  reportOptions: PropTypes.arrayOf(optionShape),
  updateEraser: functionType,
  updateFileName: functionType,
  updateMaxDensityLimits: functionType,
  updateMinDensityLimits: functionType,
  updateSegmentationDiameter: functionType,
  updateSegmentationLung: functionType,
  updateSelectedSubtype: functionType,
  updateSelectedTool: functionType,
  updateSlice: functionType,
  handleResetZoom: functionType,
  handleSave: functionType,
  handleSelectType: functionType,
  handleZoomIn: functionType,
  handleZoomOut: functionType,
};

EditToolList.defaultProps = {
  annotationListAmount: 0,
  imagesAmount: 0,
  maxDensityLimits: 0,
  minDensityLimits: 0,
  segmentationDiameter: 0,
  slice: 1,
  zoom: 1,
  eraser: false,
  lastExamId: null,
  nextExamId: null,
  reportId: null,
  segmentationLung: '',
  selectedTool: EDIT_TOOL_TYPES?.segmentation?.key,
  selectedSubtype: null,
  selectedType: null,
  annotationTypeOptions: [],
  reportOptions: [],
  updateEraser: null,
  updateFileName: null,
  updateMaxDensityLimits: null,
  updateMinDensityLimits: null,
  updateSegmentationDiameter: null,
  updateSegmentationLung: null,
  updateSelectedSubtype: null,
  updateSelectedTool: null,
  updateSlice: null,
  handleResetZoom: null,
  handleSave: null,
  handleSelectType: null,
  handleZoomIn: null,
  handleZoomOut: null,
};

export default EditToolList;
